import { useCallback, useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import {
  IconShirt,
  IconShoe,
  IconHelmet,
  IconEyeCheck,
  IconHandStop,
  IconMask,
  IconArrowDown,
  IconEar,
  IconShield,
} from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import { useSizEmployeeRequiredItems } from '@/entities/siz';
import type { SizCategory } from '@/entities/siz';
import { buildRows, type SizRowState } from './sizTabModel';
import { MOCK_REQUIRED_ITEMS } from './sizTabMocks';
import card from './EmployeeCard.module.scss';
import s from './SizTab.module.scss';

interface SizTabProps {
  employeeId: string;
}

/** Включает демо-данные `sizTabMocks.ts` для всех сотрудников. */
const SIZ_TAB_USE_MOCKS = false;

const ROWS_PAGE_SIZE = 8;

const CATEGORY_ICON: Record<SizCategory, TablerIcon> = {
  clothing: IconShirt,
  footwear: IconShoe,
  head_protection: IconHelmet,
  eye_protection: IconEyeCheck,
  hand_protection: IconHandStop,
  respiratory_protection: IconMask,
  fall_protection: IconArrowDown,
  hearing_protection: IconEar,
  other: IconShield,
};

function pluralPositions(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'позицию';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'позиции';
  return 'позиций';
}

export function SizTab({ employeeId }: SizTabProps) {
  const { data: realRequiredItems, isLoading: itemsLoading } = useSizEmployeeRequiredItems(employeeId);

  const requiredItems = SIZ_TAB_USE_MOCKS ? MOCK_REQUIRED_ITEMS : realRequiredItems;

  const [rowsLimit, setRowsLimit] = useState(ROWS_PAGE_SIZE);

  const rows = useMemo(() => buildRows(requiredItems ?? []), [requiredItems]);

  const showMoreRows = useCallback(() => {
    setRowsLimit((prev) => prev + ROWS_PAGE_SIZE);
  }, []);

  const visibleRows = rows.slice(0, rowsLimit);
  const hiddenCount = Math.max(0, rows.length - rowsLimit);

  return (
    <div className={card['root__tab-content']}>
      <span className={s['root__sectionTitle']}>Положено по нормам</span>
      <Spin spinning={itemsLoading}>
        {rows.length > 0 ? (
          <>
            {visibleRows.map((row) => (
              <SizRow key={row.item.sizItemId} row={row} />
            ))}
            {hiddenCount > 0 ? (
              <button type="button" className={s['root__more']} onClick={showMoreRows}>
                Показать ещё {hiddenCount} {pluralPositions(hiddenCount)}
              </button>
            ) : null}
          </>
        ) : (
          <Empty description="Нет данных о нормах" />
        )}
      </Spin>
    </div>
  );
}

function SizRow({ row }: { row: SizRowState }) {
  const Icon = CATEGORY_ICON[row.item.category] ?? IconShield;
  return (
    <div className={s['root__row']}>
      <span className={s['root__rowIcon']}>
        <Icon size={20} stroke={1.6} />
      </span>
      <div className={s['root__rowBody']}>
        <div className={s['root__rowTitle']}>{row.title}</div>
        {row.subtitle ? <div className={s['root__rowSubtitle']}>{row.subtitle}</div> : null}
      </div>
    </div>
  );
}
